import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}

export function formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount)
}

/**
 * Format a price value to up to 2 decimal places, trimming unnecessary trailing zeros.
 * Examples: 132 -> "132", 124.36 -> "124.36", 124.3 -> "124.3", 124.367 -> "124.37"
 */
export function formatPrice(value: number): string {
    return parseFloat(value.toFixed(2)).toString()
}

export function formatPercent(value: number | null | undefined): string {
    return `${parseFloat((value ?? 0).toFixed(2))}%`
}

export function formatDelta(delta: number): string {
    if (delta === 0) return '—'
    const prefix = delta > 0 ? '+' : ''
    return `${prefix}₹${delta}`
}

export function getPriceStatus(delta: number): 'undercut' | 'competitive' | 'premium' {
    if (delta < -10) return 'undercut'
    if (delta > 10) return 'premium'
    return 'competitive'
}

export function getStatusLabel(status: 'undercut' | 'competitive' | 'premium'): string {
    switch (status) {
        case 'undercut': return 'Undercut'
        case 'competitive': return 'Competitive'
        case 'premium': return 'Premium'
    }
}

export function formatTimeAgo(date: Date | string): string {
    const now = new Date()
    const past = new Date(date)
    const diffMs = now.getTime() - past.getTime()
    const diffMins = Math.floor(diffMs / 60000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`

    const diffHours = Math.floor(diffMins / 60)
    if (diffHours < 24) return `${diffHours}h ago`

    const diffDays = Math.floor(diffHours / 24)
    if (diffDays === 1) return 'Yesterday'
    return `${diffDays} days ago`
}

function escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Removes a leading brand from a product name, if present.
 * Examples:
 * - "Dairy Day Premium Cassata" + "Dairy Day" => "Premium Cassata"
 * - "Amul - Gold Milk" + "Amul" => "Gold Milk"
 */
export function stripBrandPrefixFromProductName(name: string, brand?: string | null): string {
    const normalizedName = name?.trim()
    const normalizedBrand = brand?.trim()

    if (!normalizedName || !normalizedBrand) return name

    const escapedBrand = escapeRegExp(normalizedBrand)
    const prefixRegex = new RegExp(`^${escapedBrand}(?=$|\\s|[-:/|(),])(?:\\s*[-:/|(),]?\\s*)?`, 'i')
    const stripped = normalizedName.replace(prefixRegex, '').trimStart()

    return stripped || normalizedName
}
